import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Close, ModeEdit } from '@mui/icons-material';
import { Box, CircularProgress, SpeedDialAction } from '@mui/material';
import { observer } from 'mobx-react-lite';
import {
  AdjustmentsList,
  AssetDetails,
  AssetPaymentSchedule,
  AssetValue,
  ConfirmModal,
  Documents,
  FuseTabs,
  Notes,
  PaymentsList,
  ViewPage,
} from '/src/components';
import { useAssetStore, useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';
import { getSpeedDialActionProps } from '/src/utils';

/**
 * View Asset Page
 */
export const ViewAssetPage = observer(() => {
  /** Context **/
  const assetStore = useAssetStore();
  const userStore = useUserStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    true, // Asset Value
    false, // Asset Details
    false, // Payment Schedule
    false, // Company Comments
    false, // Socket Comments
    false, // Documents
  ]);

  /** Computed **/
  const assetId = params.assetId;
  const asset = assetStore.currentAsset;

  /** Methods **/

  /**
   * Closes the Asset.
   *
   * @remarks
   * We don't ever remove Assets from the system, just mark them as "closed".
   *
   * @remarks
   * Redirects to the Assets List afterwards.
   */
  const close = async () => {
    if (!asset) return;

    try {
      await assetStore.close(asset?._id);

      toast.success(toastMessages.deleteAsset.success);
      navigate('/assets');
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.deleteAsset.error);
    }
  };

  /**
   * Updates the Company Comments list.
   *
   * @remarks
   * This works the same as any other Notes field. We just needed two of them.
   */
  const updateCompanyComments = async (companyComments: Note[]) => {
    if (!asset) return;

    const response = await assetStore.update({ ...asset, companyComments });
    if (!response) throw new Error(`Error updating company comments for Asset ID ${asset._id}`);

    assetStore.setCurrentAsset(await assetStore.get(asset._id));
  };

  /**
   * Update Socket Comments
   *
   * @remarks
   * This works the same as any other Notes field. We just needed two of them.
   */
  const updateSocketComments = async (socketComments: Note[]) => {
    if (!asset) return;

    const response = await assetStore.update({ ...asset, socketComments });
    if (!response) throw new Error(`Error updating socket comments for Asset ID ${asset._id}`);

    assetStore.setCurrentAsset(await assetStore.get(asset._id));
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!asset) return;

    const response = await assetStore.update({ ...asset, files });
    if (!response) throw new Error(`Error updating files for Asset ID ${asset._id}`);

    assetStore.setCurrentAsset(await assetStore.get(asset._id));
  };

  /** Effects **/

  useEffect(() => {
    assetId && assetStore.get(assetId);
  }, [assetId]);

  /** Render **/

  return (
    <ViewPage
      modelName={asset?.name ?? ''}
      modelType={DataModelType.Asset}
      sidebarFlex="25"
      contentFlex="75"
      sidebar={
        <Box flexDirection="column" sx={{ flex: 1 }}>
          <AssetValue
            asset={asset}
            open={sidebarItems[0]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(0, !sidebarItems[0]))}
          />
          <AssetDetails
            asset={asset}
            open={sidebarItems[1]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(1, !sidebarItems[1]))}
          />
          <AssetPaymentSchedule
            asset={asset}
            open={sidebarItems[2]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(2, !sidebarItems[2]))}
          />
          <Notes
            mode={CRUDMode.View}
            notes={asset?.companyComments ?? []}
            title="Notes"
            tooltip="Add Note"
            sidebarIndex={3}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateNotes={updateCompanyComments}
          />
          {userStore.isAdmin && (
            <Notes
              mode={CRUDMode.View}
              notes={asset?.socketComments ?? []}
              title="Internal Notes"
              tooltip="Add Internal Note"
              sidebarIndex={4}
              sidebarItems={sidebarItems}
              setSidebarItems={setSidebarItems}
              updateNotes={updateSocketComments}
            />
          )}
          <Documents
            mode={CRUDMode.Edit}
            documents={asset?.files ?? []}
            sidebarIndex={5}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateDocuments={updateFiles}
          />
        </Box>
      }
      speedDialActions={[
        <SpeedDialAction
          key="edit-button"
          icon={<ModeEdit className="material-icons" />}
          slotProps={getSpeedDialActionProps(CRUDMode.Edit, 'Edit Asset')}
          onClick={() => navigate(`/assets/${asset?._id}/edit`)}
        />,
        <SpeedDialAction
          key="delete-button"
          icon={<Close className="material-icons" />}
          slotProps={getSpeedDialActionProps(CRUDMode.Remove, 'Close Asset', 'Mark Asset as Closed')}
          onClick={() => setShowConfirmClose(true)}
        />,
      ]}
      modals={
        <ConfirmModal
          content="Are you sure you want to mark this Asset as Closed?"
          open={showConfirmClose}
          setOpen={setShowConfirmClose}
          onOk={() => close()}
          label="Confirm Asset Closing"
          okLabel="Close"
          cancelLabel="Cancel"
        />
      }
    >
      {asset ? (
        <FuseTabs defaultTab={0}>
          {[
            { index: 0, label: 'Payments', content: <PaymentsList query={{ assetId: asset._id }} /> },
            { index: 1, label: 'Adjustments', content: <AdjustmentsList query={{ assetId: asset._id }} /> },
          ]}
        </FuseTabs>
      ) : (
        <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
          <CircularProgress className="md-primary" md-mode="indeterminate" />
        </Box>
      )}
    </ViewPage>
  );
});
